import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactCrop, { 
  type Crop, 
  type PixelCrop,
  centerCrop,
  makeAspectCrop
} from 'react-image-crop';
import { X } from 'lucide-react';
import 'react-image-crop/dist/ReactCrop.css';
import { OroLoader } from './OroLoader';

interface AvatarCropModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageFile: File;
  onCropComplete: (croppedFile: File) => void;
}

export default function AvatarCropModal({
  isOpen,
  onClose,
  imageFile,
  onCropComplete
}: AvatarCropModalProps) {
  const [imageSrc, setImageSrc] = useState<string>('');
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [isProcessing, setIsProcessing] = useState(false);
  const [showCircularPreview, setShowCircularPreview] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Load image when file changes
  useEffect(() => {
    if (imageFile) {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          setImageSrc(e.target.result as string);
        }
      };
      reader.readAsDataURL(imageFile);
    }
  }, [imageFile]);

  // Initialize crop when image loads - optimized for mobile
  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    
    // Create a square crop in the center, optimized for mobile touch
    const minDimension = Math.min(width, height);
    // Use larger initial crop for easier mobile manipulation
    const cropSize = Math.min(minDimension * 0.8, 300);
    const crop = centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: (cropSize / width) * 100,
        },
        1, // 1:1 aspect ratio for circular avatars
        width,
        height
      ),
      width,
      height
    );

    setCrop(crop);
    setCompletedCrop({
      x: (crop.x / 100) * width,
      y: (crop.y / 100) * height,
      width: (crop.width / 100) * width,
      height: (crop.height / 100) * height,
      unit: 'px'
    });
  }, []);

  // Create cropped image file
  const createCroppedImage = useCallback(async (): Promise<File | null> => {
    if (!completedCrop || !imgRef.current) return null;

    const image = imgRef.current;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) return null;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    // Set canvas size to 200x200 for avatar
    const outputSize = 200;
    canvas.width = outputSize;
    canvas.height = outputSize;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Draw the cropped image
    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      outputSize,
      outputSize
    );

    // Convert to blob
    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(null);
            return;
          }

          const originalName = imageFile.name;
          const nameWithoutExt = originalName.substring(0, originalName.lastIndexOf('.')) || originalName;
          const ext = originalName.substring(originalName.lastIndexOf('.')) || '.jpg';
          const croppedName = `${nameWithoutExt}_avatar${ext}`;

          const file = new File([blob], croppedName, {
            type: 'image/jpeg'
          });

          resolve(file);
        },
        'image/jpeg',
        0.92 // Good quality for avatars
      );
    });
  }, [completedCrop, imageFile.name]);

  // Generate circular preview for avatar display
  const generateCircularPreview = useCallback(() => {
    if (!completedCrop || !imgRef.current || !canvasRef.current) return;

    const image = imgRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size for circular preview (200px circle)
    const size = 200;
    canvas.width = size;
    canvas.height = size;

    // Create circular clipping path
    ctx.save();
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, 0, 2 * Math.PI);
    ctx.clip();

    // Draw cropped image in circle
    ctx.drawImage(
      image,
      completedCrop.x,
      completedCrop.y,
      completedCrop.width,
      completedCrop.height,
      0,
      0,
      size,
      size
    );
    ctx.restore();
  }, [completedCrop]);

  // Update circular preview when crop changes
  useEffect(() => {
    if (showCircularPreview) {
      generateCircularPreview();
    }
  }, [showCircularPreview, completedCrop, generateCircularPreview]);

  // Handle crop completion
  const handleCropComplete = async () => {
    setIsProcessing(true);
    
    try {
      const croppedFile = await createCroppedImage();
      if (croppedFile) {
        onCropComplete(croppedFile);
      }
    } catch (error) {
      console.error('Error creating cropped image:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle close with cleanup
  const handleClose = () => {
    setImageSrc('');
    setCrop(undefined);
    setCompletedCrop(undefined);
    setIsProcessing(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80"
        onClick={handleClose}
      />
      
      {/* Compact Modal */}
      <div className="relative z-10 w-full max-w-md max-h-[85vh] bg-oro-surface border border-oro-border rounded-xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-oro-border bg-oro-surface-alt">
          <h3 className="text-lg font-semibold text-text-primary">Crop Your Avatar</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowCircularPreview(!showCircularPreview)}
              disabled={isProcessing || !completedCrop}
              className="px-3 py-1.5 text-sm font-medium bg-oro-surface border border-oro-border text-text-secondary hover:text-text-primary rounded-lg transition-colors disabled:opacity-50 min-h-11"
            >
              {showCircularPreview ? 'Edit' : 'Preview'}
            </button>
            <button
              onClick={handleClose}
              disabled={isProcessing}
              className="w-11 h-11 hover:bg-oro-border text-text-secondary hover:text-text-primary rounded-lg flex items-center justify-center transition-colors disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Instructions */}
        <div className="p-4 text-center bg-oro-surface-alt border-b border-oro-border">
          <p className="text-sm text-text-secondary">
            {showCircularPreview 
              ? 'Your avatar will appear as a circle throughout the app' 
              : 'Drag corners to resize, tap center to move selection'
            }
          </p>
        </div>
        
        {/* Crop Area */}
        <div className="flex-1 overflow-hidden p-4 flex items-center justify-center min-h-[280px] max-h-[400px]">
          {imageSrc && (
            <div className="w-full h-full flex items-center justify-center">
              {showCircularPreview ? (
                // Circular Preview Mode
                <div className="flex flex-col items-center gap-4">
                  <div className="relative">
                    <canvas
                      ref={canvasRef}
                      width={200}
                      height={200}
                      className="border-2 border-oro-border rounded-full shadow-lg"
                    />
                    <div className="absolute inset-0 rounded-full border-2 border-oro-gold/20 pointer-events-none" />
                  </div>
                  <p className="text-sm text-text-secondary text-center px-4">
                    This is how your avatar will appear in your profile and throughout the app
                  </p>
                </div>
              ) : (
                // Crop Mode with enhanced mobile touch targets
                <ReactCrop
                  crop={crop}
                  onChange={setCrop}
                  onComplete={setCompletedCrop}
                  aspect={1} // Square crop for circular avatars
                  minWidth={80} // Larger minimum for mobile
                  minHeight={80}
                  keepSelection
                  className="max-w-full max-h-full touch-manipulation"
                  style={{
                    // Enhanced touch targets for mobile
                    '--ReactCrop-resize-handle-size': '12px',
                    '--ReactCrop-resize-handle-color': 'var(--oro-gold)',
                  } as React.CSSProperties}
                >
                  <img
                    ref={imgRef}
                    src={imageSrc}
                    alt="Crop selection"
                    onLoad={onImageLoad}
                    className="max-w-full max-h-full object-contain select-none"
                    draggable={false}
                  />
                </ReactCrop>
              )}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="flex gap-3 p-4 border-t border-oro-border bg-oro-surface-alt">
          <button
            onClick={handleClose}
            disabled={isProcessing}
            className="flex-1 py-3 px-4 text-base font-medium text-text-secondary hover:text-text-primary bg-oro-surface hover:bg-oro-border rounded-lg transition-colors disabled:opacity-50 min-h-12"
          >
            Cancel
          </button>
          <button
            onClick={handleCropComplete}
            disabled={!completedCrop || isProcessing}
            className="flex-1 py-3 px-4 text-base font-medium text-black bg-oro-gold hover:bg-oro-gold/90 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2 min-h-12"
          >
            {isProcessing ? (
              <>
                <OroLoader variant="inline" color="currentColor" className="text-black" decorative />
                Processing...
              </>
            ) : (
              'Use as Avatar'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}